import { components as c } from "@/components";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { services as s } from "@/services";
import { Form } from "@/components/ui/form";
import Stepper from "@/components/common/Stepper";
import { z } from "zod";
import ContactPersonComboBox from "@/features/shared_attributes/components/ContactPersonComboBox";
import { diaryByIdQueryOptions } from "@/features/diaries";
import { useUpsertSharedAttributes } from "@/features/shared_attributes/api/upsertSharedAttributes";
import { extractEventById } from "@/features/diaries/services/extractEventById";
import dayjs from "dayjs";
import {
    useEventNavigationStore,
    useEventStepper,
} from "@/features/templates/hooks/useEventNavigationStore";
import { updateEvent } from "../../../../../../../features/events/api/updateEvent";
import { zodResolver } from "@hookform/resolvers/zod";

const sharedAttributeSchemaZ = z.object({
    shared_attribute_id: z.string().uuid(),
    reference_date: z.string(),
    contact_id: z.string().uuid(),
});

type SharedAttributeSchemaT = z.infer<typeof sharedAttributeSchemaZ>;

export const EventsForm = () => {
    const { organisationId, event } = Route.useLoaderData();
    const navigate = useNavigate();
    const getEvent = useEventNavigationStore((state) => state.getEvent);

    const form = useForm<SharedAttributeSchemaT>({
        resolver: zodResolver(sharedAttributeSchemaZ),
        defaultValues: {
            shared_attribute_id:
                event?.shared_attributes?.shared_attribute_id ?? s.uuidv4(),
            contact_id: event?.shared_attributes?.contact_id ?? undefined,
            reference_date: event?.shared_attributes?.reference_date ??
                dayjs().toISOString(),
        },
    });
    const { mutate: upsertSharedAttribute } = useUpsertSharedAttributes();
    const { steps } = useEventStepper(
        "event-details",
        event?.required_events?.[0],
    );

    function onSubmit(data: SharedAttributeSchemaT) {
        updateEvent({
            event_id: event?.event_id,
            shared_attribute_id: data.shared_attribute_id,
        });
        upsertSharedAttribute(data, {
            onSuccess: () => {
                const eventType = getEvent();
                if (eventType === "injection") {
                    const injectionId =
                        event?.injections_view?.[0]?.injection_id ?? s.uuidv4();
                    navigate({
                        to: "/new/diary/$diaryId/events/$eventId/injection/$injectionId",
                        params: { injectionId },
                    });
                } else if (eventType === "surgery") {
                    const surgeryId = event?.surgeries_view?.[0]?.surgery_id ??
                        s.uuidv4();
                    navigate({
                        to: "/new/diary/$diaryId/events/$eventId/surgery/$surgeryId",
                        params: { surgeryId },
                    });
                } else if (eventType === "education") {
                    const educationId = event?.educations?.[0]?.education_id ??
                        s.uuidv4();
                    navigate({
                        to: "/new/diary/$diaryId/events/$eventId/education/$educationId",
                        params: { educationId },
                    });
                } else if (eventType === "shockwave") {
                    const shockwaveId =
                        event?.shockwaves_view?.[0]?.shockwave_id ?? s.uuidv4();
                    navigate({
                        to: "/new/diary/$diaryId/events/$eventId/shockwave/$shockwaveId",
                        params: { shockwaveId },
                    });
                }
            },
        });
    }

    return (
        <div className="space-y-8">
            <Stepper steps={steps} />

            <Form {...form}>
                <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="form"
                >
                    <ContactPersonComboBox
                        organisationId={organisationId}
                    />
                    <c.fc.DateTime
                        name="reference_date"
                        label="Procedure Date"
                    />
                    <div className="flex justify-between gap-4 mt-8">
                        <c.ui.Button
                            gap-4
                            type="button"
                            variant="outline"
                            asChild
                        >
                            <Link to="/new/diary/$diaryId/events/$eventId">
                                Previous
                            </Link>
                        </c.ui.Button>

                        <c.fc.NextButton />
                    </div>
                </form>
            </Form>
        </div>
    );
};

export const Route = createFileRoute(
    "/_app/new/diary/$diaryId/events/$eventId/details",
)({
    component: EventsForm,
    loader: async (
        { context, params }: {
            context: any;
            params: { diaryId: string; eventId: string };
        },
    ) => {
        const { diaryId, eventId } = params;

        const diary = await context.queryClient.ensureQueryData(
            diaryByIdQueryOptions(diaryId),
        );
        if (!diary) {
            return { event: undefined, organisationId: undefined };
        }
        const event = diary ? extractEventById(diary, eventId) : undefined;
        return { event, organisationId: diary.organisation_id };
    },
});
